import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { cn, initials } from "@/lib/utils";
import SessionTimeout from "./SessionTimeout";
import ThemeToggle from "./ThemeToggle";
import {
  LayoutDashboard, Users, Activity, ListChecks, ClipboardList, HeartPulse,
  Stethoscope, Receipt, ShieldCheck, BarChart3, LogOut, Menu, X, User,
  Building2, DollarSign, BookOpen, ChevronDown, Network, Server,
  FileWarning, Headphones, Gauge, Send, PlayCircle, Presentation, FileCheck, Bot, Handshake, Rocket, Award, Globe, HelpCircle, Trophy, FileText,
} from "lucide-react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "care_manager", "provider"] },
  { to: "/tour", label: "Product Tour", icon: PlayCircle, roles: ["admin", "care_manager", "provider"] },
  { to: "/help", label: "Help & Guide", icon: HelpCircle, roles: ["admin"] },
  { to: "/compliance", label: "Security & Compliance", icon: ShieldCheck, roles: ["admin", "care_manager", "provider"] },
  { section: "Care Management", roles: ["admin", "care_manager"] },
  { to: "/members", label: "Members", icon: Users, roles: ["admin", "care_manager", "provider"] },
  { to: "/risk", label: "Risk Stratification", icon: Activity, roles: ["admin", "care_manager"] },
  { to: "/work-queue", label: "Work Queue", icon: ListChecks, roles: ["admin", "care_manager"] },
  { to: "/assessments", label: "Assessments", icon: ClipboardList, roles: ["admin", "care_manager"] },
  { to: "/care-plans", label: "Care Plans", icon: HeartPulse, roles: ["admin", "care_manager"] },
  { to: "/care-gaps", label: "Care Gaps", icon: FileWarning, roles: ["admin", "care_manager"] },
  { section: "Network & Operations", roles: ["admin", "care_manager"] },
  { to: "/providers", label: "Provider Network", icon: Stethoscope, roles: ["admin", "care_manager"] },
  { to: "/billing", label: "Billing & Claims", icon: Receipt, roles: ["admin", "care_manager"] },
  { to: "/utilization", label: "Utilization Mgmt", icon: ShieldCheck, roles: ["admin", "care_manager"] },
  { to: "/quality", label: "Quality Mgmt", icon: ShieldCheck, roles: ["admin", "care_manager"] },
  { to: "/contact-center", label: "Contact Center", icon: Headphones, roles: ["admin", "care_manager"] },
  { to: "/sla", label: "SLA & Penalties", icon: Gauge, roles: ["admin", "care_manager"] },
  { to: "/analytics", label: "Analytics", icon: BarChart3, roles: ["admin", "care_manager"] },
  { to: "/interoperability", label: "Interoperability", icon: Network, roles: ["admin", "care_manager"] },
  { to: "/edi-transport", label: "EDI & Transport", icon: Send, roles: ["admin", "care_manager"] },
  { section: "Portals", roles: ["admin", "member", "provider"] },
  { to: "/member-portal", label: "Member Portal", icon: User, roles: ["admin", "member"] },
  { to: "/provider-portal", label: "Provider Portal", icon: Building2, roles: ["admin", "provider"] },
  { section: "Business", roles: ["admin"] },
  { to: "/submission", label: "Trillium Submission Center", icon: Send, roles: ["admin"] },
  { to: "/showcase", label: "Public Showcase Page", icon: Globe, roles: ["admin"], newTab: true },
  { to: "/demo", label: "Live Demo (for Trillium)", icon: PlayCircle, roles: ["admin"], newTab: true },
  { to: "/demo-videos", label: "Demo Videos (for Trillium)", icon: PlayCircle, roles: ["admin"], newTab: true },
  { to: "/go-to-market", label: "Turnkey AI Go-To-Market", icon: Rocket, roles: ["admin"] },
  { to: "/winning-playbook", label: "Winning Playbook (Video)", icon: Trophy, roles: ["admin"] },
  { to: "/staffing", label: "Staffing & AI Stack", icon: Users, roles: ["admin"] },
  { to: "/job-descriptions", label: "Role Job Descriptions", icon: FileText, roles: ["admin"] },
  { to: "/founder-credibility", label: "Founder Credibility", icon: Award, roles: ["admin"] },
  { to: "/rfp-response", label: "Trillium RFP Response", icon: FileCheck, roles: ["admin"] },
  { to: "/delivery-model", label: "Delivery & Ops Model", icon: ClipboardList, roles: ["admin"] },
  { to: "/ai-operated-model", label: "AI-Operated Model", icon: Bot, roles: ["admin"] },
  { to: "/trillium-approach", label: "Approach & Credibility", icon: Handshake, roles: ["admin"] },
  { to: "/tenants", label: "Tenant Control Plane", icon: Server, roles: ["admin"] },
  { to: "/sales-playbook", label: "Sales Playbook", icon: Presentation, roles: ["admin"] },
  { to: "/monetization", label: "Monetization Model", icon: DollarSign, roles: ["admin"] },
  { to: "/mco-playbook", label: "MCO Expansion Playbook", icon: BookOpen, roles: ["admin"] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [tenant, setTenant] = useState(localStorage.getItem("vc_tenant") || "trillium");
  const [tenantCtx, setTenantCtx] = useState(null);
  const role = user?.role || "care_manager";
  const items = NAV.filter((n) => !n.roles || n.roles.includes(role));

  useEffect(() => {
    if (role === "admin") api.get("/tenants").then((r) => setTenants(r.data)).catch(() => {});
  }, [role]);

  useEffect(() => {
    api.get("/context").then((r) => {
      setTenantCtx(r.data.tenant);
      const hsl = r.data.tenant.brand_hsl || "244 76% 59%";
      document.documentElement.style.setProperty("--primary", hsl);
      document.documentElement.style.setProperty("--ring", hsl);
      window.dispatchEvent(new Event("vc-brand-updated"));
    }).catch(() => {});
  }, [tenant]);

  const parseHsl = (hsl) => {
    const m = (hsl || "244 76% 59%").match(/([\d.]+)\s+([\d.]+)%\s+([\d.]+)%/);
    return m ? { h: m[1], s: m[2] } : { h: "244", s: "76" };
  };
  const brand = parseHsl(tenantCtx?.brand_hsl);
  const sidebarBg = `hsl(${brand.h} ${brand.s}% 10%)`;
  const activeBg = `hsl(${brand.h} ${brand.s}% 28%)`;
  const badgeBg = `hsl(${brand.h} ${brand.s}% 40%)`;
  const tenantName = tenantCtx?.name || "Go MCO";
  const tenantInitials = tenantName.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  const switchTenant = (id) => {
    localStorage.setItem("vc_tenant", id);
    setTenant(id);
    window.location.reload();
  };

  const roleLabel = { admin: "Administrator", care_manager: "Care Manager", provider: "Provider", member: "Member" }[role];

  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-surface">
      {/* Sidebar */}
      <aside
        style={{ backgroundColor: sidebarBg }}
        className={cn(
          "fixed inset-y-0 left-0 z-40 w-64 text-white flex flex-col transition-transform duration-300 lg:translate-x-0",
          open ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex items-center gap-2.5 px-5 h-16 border-b border-white/10">
          <div style={{ backgroundColor: badgeBg }} className="h-9 w-9 rounded-lg flex items-center justify-center font-display font-extrabold text-sm shrink-0">
            {tenantInitials}
          </div>
          <div className="leading-tight min-w-0">
            <p className="font-display font-extrabold tracking-tight text-base truncate">{tenantName}</p>
            <p className="text-[9px] uppercase tracking-[0.2em] text-white/50">Powered by Go MCO</p>
          </div>
          <button className="ml-auto lg:hidden" onClick={() => setOpen(false)}><X className="h-5 w-5" /></button>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5">
          {items.map((item, i) =>
            item.section ? (
              <p key={i} className="px-3 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/40">{item.section}</p>
            ) : item.newTab ? (
              <a
                key={item.to}
                href={item.to}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors duration-200 text-white/80 hover:bg-white/5"
              >
                <item.icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
                {item.label}
              </a>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                style={({ isActive }) => (isActive ? { backgroundColor: activeBg } : {})}
                data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors duration-200",
                    isActive ? "text-white" : "text-white/80 hover:bg-white/5"
                  )
                }
              >
                <item.icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
                {item.label}
              </NavLink>
            )
          )}
        </nav>
        <div className="px-4 py-3 border-t border-white/10 text-[11px] text-brand-300">
          Built to HIPAA · SOC 2 · NIST 800-53 standards
        </div>
        <SessionTimeout />
      </aside>

      {/* Main */}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 h-16 bg-background/90 backdrop-blur-xl border-b border-border flex items-center gap-4 px-4 sm:px-6">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></button>
          <div className="hidden sm:block">
            <p className="text-xs text-muted-foreground">{tenantCtx ? `${tenantCtx.name} · ${tenantCtx.plan_types?.[0] || ""}` : "Go MCO Clinical Platform"}</p>
            <p className="text-sm font-semibold text-foreground">{tenantCtx ? `${(tenantCtx.members_count || 0).toLocaleString()} members · ${tenantCtx.region}` : "Loading tenant…"}</p>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <ThemeToggle />
            {role === "admin" && tenants.length > 0 && (
              <div className="hidden sm:flex items-center gap-2 rounded-md border border-border bg-surface px-2 py-1">
                <Server className="h-4 w-4 text-brand-700" />
                <select value={tenant} onChange={(e) => switchTenant(e.target.value)} data-testid="tenant-switcher"
                  className="bg-transparent text-sm font-medium focus:outline-none cursor-pointer">
                  {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            )}
            <div className="relative">
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted transition-colors"
              data-testid="user-menu-btn"
            >
              <div className="h-8 w-8 rounded-full bg-brand-700 text-white flex items-center justify-center text-xs font-semibold">
                {user?.picture ? <img src={user.picture} alt="" className="h-8 w-8 rounded-full object-cover" /> : initials(user?.name)}
              </div>
              <div className="hidden sm:block text-left leading-tight">
                <p className="text-sm font-semibold text-foreground">{user?.name}</p>
                <p className="text-[11px] text-muted-foreground">{roleLabel}</p>
              </div>
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            </button>
            {menuOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-card border border-border rounded-md shadow-lg py-1" onMouseLeave={() => setMenuOpen(false)}>
                <div className="px-4 py-2 border-b border-border">
                  <p className="text-sm font-semibold">{user?.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>
                <button onClick={doLogout} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-foreground hover:bg-muted" data-testid="logout-btn">
                  <LogOut className="h-4 w-4" /> Sign out
                </button>
              </div>
            )}
            </div>
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto">{children}</main>
      </div>
      {open && <div className="fixed inset-0 z-30 bg-slate-900/40 lg:hidden" onClick={() => setOpen(false)} />}
    </div>
  );
}
